import { IEventRepository } from '@domain/events/IEventRepository';
import { Event } from '@domain/events/models/Event';
import { Resource } from '@domain/base/Resource';
import { EventDataSource } from '../datasources/EventDataSource';
import { EventMapper } from '../mappers/EventMapper';
import { CreateEventRequestDTO, UpdateEventRequestDTO } from '../dto/EventDTO';

export class EventRepository implements IEventRepository {
  constructor(private dataSource: EventDataSource) {}

  async getEvents(): Promise<Resource<Event[]>> {
    try {
      const events = await this.dataSource.getEvents();
      return {
        status: 'success',
        data: events.map(EventMapper.toModel)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get events' }
      };
    }
  }

  async getEvent(id: string): Promise<Resource<Event>> {
    try {
      const event = await this.dataSource.getEvent(id);
      if (!event) {
        return {
          status: 'error',
          error: { type: 'UnknownError', message: 'Event not found' }
        };
      }
      return {
        status: 'success',
        data: EventMapper.toModel(event)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get event' }
      };
    }
  }

  async createEvent(event: CreateEventRequestDTO): Promise<Resource<Event>> {
    try {
      const createdEvent = await this.dataSource.createEvent(event);
      return {
        status: 'success',
        data: EventMapper.toModel(createdEvent)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to create event' }
      };
    }
  }

  async updateEvent(id: string, event: UpdateEventRequestDTO): Promise<Resource<Event>> {
    try {
      const updatedEvent = await this.dataSource.updateEvent(id, event);
      return {
        status: 'success',
        data: EventMapper.toModel(updatedEvent)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to update event' }
      };
    }
  }

  async deleteEvent(id: string): Promise<Resource<void>> {
    try {
      await this.dataSource.deleteEvent(id);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to delete event' }
      };
    }
  }

  async getEventProducts(eventId: string): Promise<Resource<string[]>> {
    try {
      const productIds = await this.dataSource.getEventProducts(eventId);
      return {
        status: 'success',
        data: productIds
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get event products' }
      };
    }
  }

  async linkProduct(eventId: string, productIds: string[]): Promise<Resource<void>> {
    try {
      await this.dataSource.linkProduct(eventId, productIds);
      return {
        status: 'success',
        data: undefined
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to link product' }
      };
    }
  }

  async unlinkProduct(eventId: string, productIds: string[]): Promise<Resource<void>> {
    try {
      await this.dataSource.unlinkProduct(eventId, productIds);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to unlink product' }
      };
    }
  }

  async getProductEvents(productId: string): Promise<Resource<Event[]>> {
    try {
      const events = await this.dataSource.getProductEvents(productId);
      return {
        status: 'success',
        data: events.map(EventMapper.toModel)
      };
    } catch (error) {
      return {
        status: 'error',
        error: { type: 'UnknownError', message: 'Failed to get product events' }
      };
    }
  }
}

